import { useState } from "react";
import { complianceApi } from "@/lib/api";
import { VEHICLE_CLASSES } from "@/lib/constants";
import { Scales, CheckCircle, Warning, ArrowsClockwise, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LLSelect } from "@/components/ui/ll-select";
import BridgeFormulaCalc from "@/components/compliance/BridgeFormulaCalc";

const PRESETS = [
  { label: "Empty Single Unit", values: { vehicle_class: "class_5", steer_axle_lbs: 8000, drive_axle_lbs: 0, trailer_axle_lbs: 0, num_axles: 2 } },
  { label: "Loaded Tri-axle Dump", values: { vehicle_class: "class_8", steer_axle_lbs: 12000, drive_axle_lbs: 34000, trailer_axle_lbs: 20000, num_axles: 5 } },
  { label: "Tractor + Trailer", values: { vehicle_class: "class_8", steer_axle_lbs: 12000, drive_axle_lbs: 34000, trailer_axle_lbs: 34000, num_axles: 5 } },
];

export default function CompliancePage() {
  const [form, setForm] = useState({
    vehicle_class: "class_8",
    steer_axle_lbs: 12000,
    drive_axle_lbs: 34000,
    trailer_axle_lbs: 30000,
    num_axles: 5,
  });
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [mode, setMode] = useState("simple"); // 'simple' | 'bridge'

  const update = (key, val) => setForm({ ...form, [key]: val });

  const calculate = async () => {
    setCalculating(true);
    try {
      const data = await complianceApi.calculate({
        ...form,
        steer_axle_lbs: parseInt(form.steer_axle_lbs) || 0,
        drive_axle_lbs: parseInt(form.drive_axle_lbs) || 0,
        trailer_axle_lbs: parseInt(form.trailer_axle_lbs) || 0,
        num_axles: parseInt(form.num_axles) || 0,
      });
      setResult(data);
      if (!data.is_compliant) {
        toast.error(`Compliance Violation: ${data.overweight_amount.toLocaleString()} lbs over`);
      } else {
        toast.success("Vehicle is CDL Compliant");
      }
    } catch (e) {
      toast.error("Compliance check failed");
    } finally {
      setCalculating(false);
    }
  };

  const gross = (parseInt(form.steer_axle_lbs) || 0) + (parseInt(form.drive_axle_lbs) || 0) + (parseInt(form.trailer_axle_lbs) || 0);

  return (
    <div className="h-[calc(100vh-72px)] overflow-auto" data-testid="compliance-page">
      <div className="max-w-7xl mx-auto p-4 lg:p-8">
        {/* Mode tabs */}
        <div className="flex items-center border border-[#2a2a2a] mb-6 w-fit">
          <button
            onClick={() => setMode("simple")}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-2 transition-colors ${
              mode === "simple" ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:text-white"
            }`}
            data-testid="mode-simple"
          >
            <Scales size={12} weight="bold" /> Quick Axle Check
          </button>
          <button
            onClick={() => setMode("bridge")}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-2 transition-colors ${
              mode === "bridge" ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:text-white"
            }`}
            data-testid="mode-bridge"
          >
            <Truck size={12} weight="bold" /> Federal Bridge Formula
          </button>
        </div>

        {mode === "bridge" ? (
          <BridgeFormulaCalc />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Calculator Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#0d0d0d] border border-[#2a2a2a]">
              <div className="border-b border-[#2a2a2a] p-5 flex items-center gap-3">
                <Scales size={22} className="text-[#ff8c00]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">CDL Federal Bridge Formula</div>
                  <h2 className="font-heading font-black text-xl tracking-tight">Weight Compliance Calculator</h2>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* Presets */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2">Quick Presets</div>
                  <div className="flex flex-wrap gap-2">
                    {PRESETS.map((p) => (
                      <button
                        key={p.label}
                        onClick={() => setForm(p.values)}
                        className="px-3 py-1.5 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
                        data-testid={`preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">Vehicle Class</label>
                  <LLSelect
                    value={form.vehicle_class}
                    onChange={(v) => update("vehicle_class", v)}
                    testId="vehicle-class"
                    className="!w-full text-sm normal-case tracking-normal py-2.5"
                    options={VEHICLE_CLASSES.map((c) => ({ value: c.value, label: c.label }))}
                  />
                </div>

                <AxleInput label="Steer Axle" value={form.steer_axle_lbs} max={20000} onChange={(v) => update("steer_axle_lbs", v)} testId="steer-axle" />
                <AxleInput label="Drive Axle" value={form.drive_axle_lbs} max={50000} onChange={(v) => update("drive_axle_lbs", v)} testId="drive-axle" />
                <AxleInput label="Trailer Axle" value={form.trailer_axle_lbs} max={50000} onChange={(v) => update("trailer_axle_lbs", v)} testId="trailer-axle" />

                <div className="flex items-center justify-between pt-3 border-t border-[#2a2a2a]">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Gross Vehicle Weight</div>
                    <div className="font-heading font-black text-3xl mt-1 font-mono" data-testid="gross-display">
                      {gross.toLocaleString()} <span className="text-sm text-[#71717a] font-normal">LBS</span>
                    </div>
                  </div>
                  <button
                    onClick={calculate}
                    disabled={calculating}
                    className="px-6 py-3 bg-[#ff8c00] text-black hover:bg-[#e67e00] disabled:opacity-50 text-xs uppercase tracking-[0.2em] font-bold transition-colors flex items-center gap-2"
                    data-testid="calculate-btn"
                  >
                    {calculating ? <ArrowsClockwise size={16} className="animate-spin" /> : <Scales size={16} />}
                    Run Check
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Result Panel */}
          <div className="lg:col-span-2">
            {result ? (
              <ResultCard result={result} />
            ) : (
              <div className="bg-[#0d0d0d] border border-dashed border-[#2a2a2a] p-12 text-center">
                <Scales size={36} className="mx-auto text-[#71717a] mb-3" />
                <div className="text-xs uppercase tracking-[0.2em] text-[#71717a]">
                  Enter axle weights and run check
                </div>
              </div>
            )}

            {/* CDL Reference */}
            <div className="bg-[#0d0d0d] border border-[#2a2a2a] mt-4">
              <div className="border-b border-[#2a2a2a] p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Federal Reference</div>
                <div className="font-heading font-bold text-sm mt-1">CDL Weight Limits</div>
              </div>
              <div className="p-4 space-y-2 text-xs">
                <Row label="Single Axle (Federal)" value="20,000 lbs" />
                <Row label="Tandem Axle (Federal)" value="34,000 lbs" />
                <Row label="Gross Vehicle (Federal)" value="80,000 lbs" />
                <Row label="Steer Axle (Most States)" value="12,000 lbs" />
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function AxleInput({ label, value, max, onChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">{label}</label>
        <span className="font-mono text-xs text-[#a1a1aa]">{Number(value).toLocaleString()} LBS</span>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={max}
          step={500}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 accent-[#ff8c00]"
          data-testid={`${testId}-slider`}
        />
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-28 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
}

function ResultCard({ result }) {
  const compliant = result.is_compliant;
  const grossPct = Math.min(100, Math.round((result.gross_weight_lbs / result.max_gross_lbs) * 100));

  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="compliance-result">
      <div
        className="p-5 border-b-2"
        style={{
          background: compliant ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)",
          borderBottomColor: compliant ? "#22c55e" : "#ef4444",
        }}
      >
        <div className="flex items-center gap-3">
          {compliant ? (
            <CheckCircle size={32} weight="fill" className="text-[#22c55e]" />
          ) : (
            <Warning size={32} weight="fill" className="text-[#ef4444] ll-status-blink" />
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: compliant ? "#22c55e" : "#ef4444" }}>
              {compliant ? "Compliant" : "Violation"}
            </div>
            <div className="font-heading font-black text-xl tracking-tight">
              {compliant ? "CDL Pass" : `Over by ${result.overweight_amount.toLocaleString()} lbs`}
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Gross Gauge */}
        <div>
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="uppercase tracking-wider text-[#a1a1aa]">Gross / Max</span>
            <span className="font-mono font-bold">
              {result.gross_weight_lbs.toLocaleString()} / {result.max_gross_lbs.toLocaleString()}
            </span>
          </div>
          <div className="h-4 bg-[#1a1a1a] border border-[#2a2a2a] overflow-hidden relative">
            <div
              className="h-full transition-all"
              style={{
                width: `${grossPct}%`,
                background: grossPct > 100 ? "#ef4444" : grossPct > 90 ? "#ff8c00" : "#22c55e",
              }}
            ></div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] mt-1 font-mono">
            {grossPct}% of legal limit
          </div>
        </div>

        {/* Axle status */}
        <div className="space-y-2 pt-2 border-t border-[#2a2a2a]">
          <AxleStatus label="Steer Axle" status={result.steer_status} />
          <AxleStatus label="Drive Axle" status={result.drive_status} />
          <AxleStatus label="Trailer Axle" status={result.trailer_status} />
          <div className="flex items-center justify-between text-xs uppercase tracking-wider pt-2 border-t border-[#2a2a2a]">
            <span className="text-[#a1a1aa]">Bridge Formula</span>
            <span style={{ color: result.bridge_formula_ok ? "#22c55e" : "#ef4444" }} className="font-bold">
              {result.bridge_formula_ok ? "PASS" : "FAIL"}
            </span>
          </div>
        </div>

        {result.warnings.length > 0 && (
          <div className="pt-3 border-t border-[#2a2a2a]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#ef4444] mb-2 flex items-center gap-1">
              <Warning size={12} weight="fill" /> Warnings
            </div>
            <ul className="space-y-1">
              {result.warnings.map((w, i) => (
                <li key={i} className="text-xs text-[#ef4444] flex items-start gap-2">
                  <span>—</span> {w}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function AxleStatus({ label, status }) {
  const ok = status === "ok";
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="uppercase tracking-wider text-[#a1a1aa]">{label}</span>
      <span
        className="px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold"
        style={{
          color: ok ? "#22c55e" : "#ef4444",
          background: ok ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.12)",
          border: `1px solid ${ok ? "#22c55e" : "#ef4444"}`,
        }}
      >
        {ok ? "OK" : "OVER"}
      </span>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#a1a1aa] uppercase tracking-wider text-[10px]">{label}</span>
      <span className="font-mono font-bold">{value}</span>
    </div>
  );
}
